import { Link } from 'react-router-dom';
import {
  Users,
  CalendarDays,
  Ticket,
  AlertTriangle,
  ArrowRight,
  Loader2,
  MapPin,
  Image,
  Tag,
  Rocket,
  TrendingDown,
  ExternalLink,
  ChevronRight,
} from 'lucide-react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useAdminControlPanel } from '@/hooks/useAdminControlPanel';

export default function AdminControlPanelPage() {
  const { data, isLoading, error } = useAdminControlPanel();

  if (error) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center py-20">
          <AlertTriangle className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground">Kunde inte ladda kontrollpanelen.</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Kontrollpanel</h1>
          <p className="text-muted-foreground mt-1">Operativ översikt — Farmly idag</p>
        </div>

        {/* 1. KPI Row */}
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          <KPICard
            label="Publicerade värdar"
            value={data?.kpi.published_producers}
            icon={Users}
            loading={isLoading}
            link="/app/admin/producers?segment=public"
          />
          <KPICard
            label="Aktiva aktiviteter"
            value={data?.kpi.active_events}
            icon={CalendarDays}
            loading={isLoading}
          />
          <KPICard
            label="Bokningar (30 dagar)"
            value={data?.kpi.bookings_30d}
            icon={Ticket}
            loading={isLoading}
          />
          <KPICard
            label="Behöver åtgärd"
            value={data?.kpi.needs_action}
            icon={AlertTriangle}
            iconClassName="text-amber-500"
            loading={isLoading}
            link="/app/admin/producers?segment=attention"
          />
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* 2. Producer Funnel */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-medium">Värdtratt</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {isLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
              ) : data ? (
                <FunnelChart funnel={data.funnel} />
              ) : null}
            </CardContent>
          </Card>

          {/* 3. Vanligaste blockerare */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-medium">Vanligaste blockerare</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {isLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
              ) : data ? (
                <>
                  <BlockerRow icon={MapPin} label="Saknar kopplad plats" count={data.blockers.missing_place} severity="high" link="/app/admin/producers?segment=attention" />
                  <BlockerRow icon={Tag} label="Saknar kategori" count={data.blockers.missing_category} severity="high" link="/app/admin/producers?segment=attention" />
                  <BlockerRow icon={Image} label="Saknar omslagsbild" count={data.blockers.missing_image} severity="medium" link="/app/admin/producers?segment=attention" />
                  <BlockerRow icon={CalendarDays} label="Saknar aktivitet" count={data.blockers.missing_activity} severity="low" link="/app/admin/producers?segment=attention" />
                  <BlockerRow icon={Rocket} label="Redo att publiceras" count={data.blockers.ready_to_publish} severity="positive" link="/app/admin/producers?segment=draft" />
                </>
              ) : null}
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* 4. Stagnerade värdar */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-medium">Stagnerade värdar</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {isLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
              ) : data ? (
                <>
                  <StagnatedRow label="Publicerade utan aktivitet" count={data.stagnated.public_no_activity} link="/app/admin/producers?segment=public" />
                  <StagnatedRow label="Aktivitet utan bokning" count={data.stagnated.activity_no_booking} />
                  <StagnatedRow label="Ingen rörelse på 60 dagar" count={data.stagnated.inactive_60d} />
                </>
              ) : null}
            </CardContent>
          </Card>

          {/* 5. Snabblänkar */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-medium">Snabblänkar</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <ActionLink label="Värdar som behöver åtgärd" to="/app/admin/producers?segment=attention" />
              <ActionLink label="Värdöversikt" to="/app/admin/producers" />
              <ActionLink label="Intresseanmälningar" to="/app/admin/producer-interest" />
              <ActionLink label="Systemöversikt" to="/app/admin/dashboard" />
              <ActionLink label="Host Debug" to="/app/admin/system/host-debug" />
              <ActionLink label="Host Health" to="/app/admin/system/host-health" />
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}

/* ── Sub-components ─────────────────────────────────────────── */

function KPICard({ label, value, icon: Icon, iconClassName, loading, link }: {
  label: string;
  value?: number;
  icon: React.ComponentType<{ className?: string }>;
  iconClassName?: string;
  loading: boolean;
  link?: string;
}) {
  const card = (
    <Card className={link ? 'hover:bg-muted/50 transition-colors cursor-pointer' : ''}>
      <CardContent className="pt-5 pb-4 px-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-muted-foreground">{label}</span>
          <Icon className={`h-4 w-4 ${iconClassName || 'text-muted-foreground'}`} />
        </div>
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : (
          <span className="text-2xl font-bold text-foreground">{value ?? 0}</span>
        )}
      </CardContent>
    </Card>
  );
  return link ? <Link to={link}>{card}</Link> : card;
}

const FUNNEL_STEPS: { key: keyof import('@/hooks/useAdminControlPanel').ControlPanelFunnel; label: string }[] = [
  { key: 'interest_sent', label: 'Intresse skickat' },
  { key: 'accepted', label: 'Accepterad' },
  { key: 'linked_place', label: 'Kopplad plats' },
  { key: 'published', label: 'Publicerad' },
  { key: 'has_activity', label: 'Har aktivitet' },
  { key: 'has_booking', label: 'Har bokning' },
];

function FunnelChart({ funnel }: { funnel: import('@/hooks/useAdminControlPanel').ControlPanelFunnel }) {
  const max = Math.max(funnel.interest_sent, 1);
  return (
    <div className="space-y-3">
      {FUNNEL_STEPS.map((step, i) => {
        const val = funnel[step.key];
        const pct = Math.round((val / max) * 100);
        const prev = i > 0 ? funnel[FUNNEL_STEPS[i - 1].key] : null;
        const dropoff = prev !== null && prev > 0 ? Math.round(((prev - val) / prev) * 100) : null;
        return (
          <div key={step.key}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-foreground">{step.label}</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-foreground">{val}</span>
                {dropoff !== null && dropoff > 0 && (
                  <span className="text-xs text-muted-foreground">
                    −{dropoff}%
                  </span>
                )}
              </div>
            </div>
            <Progress value={pct} className="h-2" />
          </div>
        );
      })}
    </div>
  );
}

function BlockerRow({ icon: Icon, label, count, severity, link }: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  count: number;
  severity: 'high' | 'medium' | 'low' | 'positive';
  link?: string;
}) {
  const colors = {
    high: 'text-destructive',
    medium: 'text-amber-500',
    low: 'text-muted-foreground',
    positive: 'text-primary',
  };

  const content = (
    <div className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors">
      <div className="flex items-center gap-3">
        <Icon className={`h-4 w-4 ${colors[severity]}`} />
        <span className="text-sm text-foreground">{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className={`text-sm font-semibold ${colors[severity]}`}>{count}</span>
        {link && <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
      </div>
    </div>
  );

  return link ? <Link to={link}>{content}</Link> : content;
}

function StagnatedRow({ label, count, link }: { label: string; count: number; link?: string }) {
  const content = (
    <div className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors">
      <div className="flex items-center gap-3">
        <TrendingDown className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm text-foreground">{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-foreground">{count}</span>
        {link && <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
      </div>
    </div>
  );
  return link ? <Link to={link}>{content}</Link> : content;
}

function ActionLink({ label, to }: { label: string; to: string }) {
  return (
    <Link to={to} className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors group">
      <span className="text-sm text-foreground">{label}</span>
      <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
    </Link>
  );
}
